import React, { useCallback, useState, useMemo, FormEvent } from 'react';
import * as Sentry from '@sentry/react';
import { RouteComponentProps } from 'react-router-dom';
import { RouteDefinitions } from 'routes';
import { useAuth } from 'shared/providers/AuthProvider';
import DefaultPublic from 'shared/components/Layouts/DefaultPublic';
import { Heading, Text, Box, Flex, Button, VStack, Progress } from '@televet/kibble-ui';
import { useAppDispatch } from 'state/hooks';
import { updateCurrentClinicState } from 'state/currentClinic/currentClinicSlice';
import { useApolloClient } from '@apollo/client';
import { GetClinicUserDocument } from 'shared/types/graphql';
import { parseFullName } from 'shared/utils';

const Impersonate = ({ history, match, location }: RouteComponentProps<{ token: string }>): JSX.Element => {
  const { token } = match.params;
  const { loginWithToken, logout } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const apolloClient = useApolloClient();

  // Get necessary impersonation info from query params
  const { clinicId, email, firstName, lastName } = useMemo(() => {
    const fullName = new URLSearchParams(location.search).get('impersonatingUserName') ?? '';
    const { firstName, lastName } = parseFullName(fullName);

    return {
      clinicId: new URLSearchParams(location.search).get('clinicId') ?? undefined,
      email: new URLSearchParams(location.search).get('impersonatingUserEmail') ?? '',
      firstName,
      lastName,
    };
  }, [location.search]);

  const impersonateUser = useCallback(
    async (token: string): Promise<void> => {
      try {
        setIsLoading(true);
        dispatch(
          updateCurrentClinicState({
            clinicId,
            isImpersonating: true,
            impersonatingUser: { email, firstName, lastName, isAdmin: true },
          }),
        );
        await loginWithToken(token, true);
        await apolloClient.refetchQueries({
          include: [GetClinicUserDocument],
          updateCache(cache) {
            cache.modify({
              fields: {
                getCurrentUser(value, { INVALIDATE }) {
                  return INVALIDATE;
                },
              },
            });
          },
        });
        history.push(RouteDefinitions.Index);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, clinicId, email, firstName, lastName, loginWithToken, apolloClient, history],
  );

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (token) {
      impersonateUser(token);
    }
  };

  const onCancel = (): void => {
    logout();
  };

  return (
    <DefaultPublic>
      <VStack spacing={4}>
        <Box width="320px">
          {isLoading ? (
            <>
              <Heading size="md">Loading...</Heading>
              <Progress mt="25px" size="xs" colorScheme="primary" isIndeterminate />
            </>
          ) : (
            <>
              <Heading fontWeight="bold" size="md" lineHeight="30px">
                Impersonate This User
              </Heading>
              <Text size="md" mt="12px">
                Click the button below to confirm that you would like to impersonate this user.
              </Text>

              <Box width="320px" mt="18px">
                <Flex justifyContent="space-between" alignContent="space-between">
                  <Button
                    onClick={onSubmit}
                    variant="primary"
                    data-testid="impersonate-button"
                    name="impersonate-button"
                  >
                    Confirm
                  </Button>
                  <Button variant="ghost" onClick={onCancel}>
                    Cancel
                  </Button>
                </Flex>
              </Box>
            </>
          )}
        </Box>
      </VStack>
    </DefaultPublic>
  );
};

export default Impersonate;
